import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  useTheme,
  CircularProgress,
  Button,
  TextField,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  ShoppingCart as OrderIcon,
} from "@mui/icons-material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import { useDispatch } from "react-redux";
import { getMyntraStatistics } from "app/redux/slice/myntraSlice";
import { useSnackbar } from "notistack";
import Loader from "@jumbo/components/Loader";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { useNavigate } from "react-router-dom";
import { myntraStatisticsList } from "app/routes/constant";
import moment from "moment";

const PRIMARY_STATUSES = ["DELIVERED", "RETURN_INITIATED", "RETURN_RECEIVED", "CANCELLED"];

const MyntraStatistics = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [statistics, setStatistics] = useState(null);
  const [statusBreakdown, setStatusBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [totalOrder, setTotalOrder] = useState(null);

  useEffect(() => {
    fetchStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilter = () => {
    setLoading(true);
    if (dateRange[0] === null || dateRange[1] === null) {
      enqueueSnackbar("Please select both start and end date", {
        variant: "error",
      });
      setLoading(false);
      return;
    }
    const params = {
      fromDate: moment(dateRange[0]).format("YYYY-MM-DD"),
      toDate: moment(dateRange[1]).format("YYYY-MM-DD"),
    };
    dispatch(getMyntraStatistics(params))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          processStatisticsData(res?.data?.data);
        } else {
          enqueueSnackbar("Failed to fetch statistics", { variant: "error" });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        enqueueSnackbar("Failed to fetch statistics", { variant: "error" });
      });
  };

  const fetchStatistics = () => {
    setLoading(true);
    setDateRange([null, null]);
    dispatch(getMyntraStatistics({}))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          processStatisticsData(res?.data?.data);
        } else {
          enqueueSnackbar("Failed to fetch statistics", { variant: "error" });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        enqueueSnackbar("Failed to fetch statistics", { variant: "error" });
      });
  };

  const processStatisticsData = (data) => {
    setTotalOrder(data?.totalOrders);
    const breakdown = data?.statusBreakdown || [];
    const primary = breakdown.filter((i) =>
      PRIMARY_STATUSES.includes(i?._id)
    );
    const rest = breakdown.filter(
      (i) => !PRIMARY_STATUSES.includes(i?._id)
    );
    setStatistics(primary);
    setStatusBreakdown(rest);
  };

  const getStatusIcon = (status) => {
    const map = {
      DELIVERED: <CheckCircleIcon color="success" />,
      CANCELLED: <CancelIcon color="error" />,
      SHIPPED: <LocalShippingIcon color="info" />,
      RETURN_INITIATED: <AssignmentReturnIcon color="warning" />,
      RETURN_RECEIVED: <AssignmentReturnIcon color="info" />,
      RETURN_COMPLETED: <AssignmentReturnIcon color="secondary" />,
      RETURN_REJECTED: <CancelIcon color="error" />,
      EXCHANGE_INITIATED: <SwapHorizIcon color="warning" />,
      EXCHANGE_COMPLETED: <SwapHorizIcon color="success" />,
      READY_TO_DISPATCH: <OrderIcon color="primary" />,
      PENDING: <PendingActionsIcon color="default" />,
      LOST: <OrderIcon color="error" />,
    };
    return map[status] || <OrderIcon />;
  };

  const getStatusColor = (status) => {
    const map = {
      DELIVERED: theme.palette.success.main,
      CANCELLED: theme.palette.error.main,
      SHIPPED: theme.palette.info.main,
      RETURN_INITIATED: theme.palette.warning.main,
      RETURN_RECEIVED: theme.palette.info.main,
      RETURN_COMPLETED: theme.palette.secondary.main,
      RETURN_REJECTED: theme.palette.error.main,
      EXCHANGE_INITIATED: theme.palette.warning.light,
      EXCHANGE_COMPLETED: theme.palette.success.light,
      READY_TO_DISPATCH: theme.palette.primary.main,
      PENDING: theme.palette.grey[400],
      LOST: theme.palette.error.main,
    };
    return map[status] || theme.palette.grey[500];
  };

  const formatStatusLabel = (status) => {
    const map = {
      DELIVERED: "Delivered",
      CANCELLED: "Cancelled",
      SHIPPED: "Shipped",
      RETURN_INITIATED: "Return Initiated",
      RETURN_RECEIVED: "Return Received",
      RETURN_COMPLETED: "Return Completed",
      RETURN_REJECTED: "Return Rejected",
      EXCHANGE_INITIATED: "Exchange Initiated",
      EXCHANGE_COMPLETED: "Exchange Completed",
      READY_TO_DISPATCH: "Ready to Dispatch",
      PENDING: "Pending",
      LOST: "Lost",
    };
    return map[status] || status;
  };

  const StatusCard = ({ status }) => (
    <Paper
      sx={{
        p: 2,
        textAlign: "center",
        cursor: "pointer",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
        },
      }}
      onClick={() => {
        let params = null;
        if (dateRange[0] !== null && dateRange[1] !== null) {
          params = {
            fromDate: moment(dateRange[0]).format("YYYY-MM-DD"),
            toDate: moment(dateRange[1]).format("YYYY-MM-DD"),
          };
        }
        navigate(myntraStatisticsList(status?._id), { state: params });
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
        }}
      >
        <Box sx={{ mr: 1, color: getStatusColor(status._id) }}>
          {getStatusIcon(status._id)}
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {formatStatusLabel(status._id)}
        </Typography>
      </Box>
      <Typography variant="h4" color={getStatusColor(status._id)}>
        {status.orderCount ?? status.count}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {status.percentage}% of total
      </Typography>
    </Paper>
  );

  return (
    <Box>
      <Typography variant="h4" component="h1" mb={2}>
        Myntra Statistics
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={dateRange}
                  onChange={setDateRange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                onClick={handleApplyFilter}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : null}
                fullWidth
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="outlined"
                onClick={fetchStatistics}
                disabled={loading}
                startIcon={<RefreshIcon />}
                fullWidth
              >
                Reset
              </Button>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="h5">
                Total Unique Orders: {totalOrder ?? 0}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Loader />
      ) : (
        <Grid container spacing={3}>
          {/* Primary Status Cards */}
          {statistics?.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom>
                Key Metrics
              </Typography>
              <Grid container spacing={3}>
                {statistics.map((status) => (
                  <Grid item xs={12} sm={6} md={3} key={status?._id}>
                    <StatusCard status={status} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          {/* Status Breakdown Cards */}
          {statusBreakdown?.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
                Status Breakdown
              </Typography>
              <Grid container spacing={3}>
                {statusBreakdown.map((status) => (
                  <Grid item xs={12} sm={6} md={3} key={status?._id}>
                    <StatusCard status={status} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default MyntraStatistics;
